/**
 * MilestoneCard — renders a single milestone with earned/locked state
 */

import { MilestoneDefinition } from '@/types';

interface MilestoneCardProps {
  milestone: MilestoneDefinition;
  earned: boolean;
  /** Current progress value (e.g., 3 out of 10) */
  progress?: number;
  /** Target value for the progress bar */
  target?: number;
}

export function MilestoneCard({ milestone, earned, progress, target }: MilestoneCardProps) {
  const rewardText = milestone.rewards.map((r) => r.description).join(', ');
  const hasProgress = !earned && progress !== undefined && target !== undefined && target > 0;

  return (
    <div
      className={`flex items-start gap-3 rounded-lg p-3 border-l-4 ${
        earned
          ? 'border-green-500 bg-gray-800/80'
          : 'border-gray-600 bg-gray-800/40 opacity-70'
      }`}
    >
      {/* Icon */}
      <div className="text-2xl shrink-0 mt-0.5">
        {earned ? milestone.icon : '🔒'}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className={`font-semibold text-sm ${earned ? 'text-white' : 'text-gray-400'}`}>
            {milestone.name}
          </h3>
          {earned && (
            <span className="text-green-400 text-xs shrink-0">Earned</span>
          )}
        </div>

        <p className={`text-xs mt-0.5 ${earned ? 'text-gray-300' : 'text-gray-500'}`}>
          {milestone.description}
        </p>

        {/* Reward line */}
        <div className={`text-xs mt-1 ${earned ? 'text-yellow-400' : 'text-gray-500'}`}>
          {rewardText}
        </div>

        {/* Progress bar for locked milestones */}
        {hasProgress && (
          <div className="mt-2">
            <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
              <span>Progress</span>
              <span>{progress} / {target}</span>
            </div>
            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, (progress / target) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
